import { useLayoutEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';

import { AppMessages } from 'i18n/messages';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { updateOrganization } from 'api/organizations/organizations';
import { getObjectInNestedObjectByKey } from 'helpers/getObjectInNestedObjectByKey';
import { useMutation } from 'hooks/useMutation/useMutation';

import { OrganizationDetails } from './OrganizationDetails';
import { OrganizationDetailsContainerProps, UpdateOrganizationFormBody } from './OrganizationDetails.types';
import { useOrganizationDetailsValidation } from './OrganizationDetails.validation';

export const OrganizationDetailsContainer = ({
  mainOrganization,
  selectedOrganization,
  setOrganizations,
  organizations,
}: OrganizationDetailsContainerProps) => {
  const { formatMessage } = useLocale();
  const queryClient = useQueryClient();
  const { schema } = useOrganizationDetailsValidation(mainOrganization, selectedOrganization);

  const { mutate: onUpdateOrganization, isLoading } = useMutation(updateOrganization, {
    onSuccess: (_, { body }) => {
      queryClient.invalidateQueries([QueryKeyEnum.ORGANIZATIONS]);
      queryClient.invalidateQueries([QueryKeyEnum.ORGANIZATIONS_FOR_USER]);

      const isNameAndCodeChanged = selectedOrganization.nazwa !== body.nazwa && selectedOrganization.kod !== body.kod;
      const isOnlyNameChanged = selectedOrganization.nazwa !== body.nazwa && selectedOrganization.kod === body.kod;
      const isOnlyCodeChanged = selectedOrganization.kod !== body.kod && selectedOrganization.nazwa === body.nazwa;
      const isOrderChanged = selectedOrganization.order !== body.order;

      if (isOrderChanged) {
        toast.dark(formatMessage({ id: AppMessages['definitions.organization.details.edit.success.order'] }));
      }

      if (isNameAndCodeChanged) {
        toast.dark(formatMessage({ id: AppMessages['definitions.organization.details.edit.success.default'] }));
        return;
      }

      if (isNameAndCodeChanged) {
        toast.dark(formatMessage({ id: AppMessages['definitions.organization.details.edit.success.default'] }));
        return;
      }

      if (isOnlyNameChanged) {
        toast.dark(formatMessage({ id: AppMessages['definitions.organization.details.edit.success.name'] }));
        return;
      }

      if (isOnlyCodeChanged) {
        toast.dark(formatMessage({ id: AppMessages['definitions.organization.details.edit.success.code'] }));
        return;
      }
    },
  });

  const formMethods = useForm<UpdateOrganizationFormBody>({
    mode: 'all',
    defaultValues: selectedOrganization,
    resolver: yupResolver(schema),
  });

  useLayoutEffect(() => {
    const updatedOrganizationItem = getObjectInNestedObjectByKey(
      mainOrganization,
      'id',
      selectedOrganization.id,
    )?.object;

    formMethods.reset({ ...updatedOrganizationItem });
  }, [selectedOrganization, mainOrganization]);

  const onSubmit = (body: UpdateOrganizationFormBody) => {
    onUpdateOrganization({
      id: selectedOrganization.id,
      body: { nazwa: body.nazwa, kod: body.kod, order: body.order },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <OrganizationDetails
        mainOrganization={mainOrganization}
        isLoading={isLoading}
        selectedOrganization={selectedOrganization}
        onSubmit={onSubmit}
        setOrganizations={setOrganizations}
        organizations={organizations}
      />
    </FormProvider>
  );
};
